import React, { useState } from 'react';
import { FaEye, FaEyeSlash, FaDownload, FaTimes } from 'react-icons/fa';
import { ActionButton } from './Forms/ActionButton';
import { baseUrl } from '../api/baseUrl';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const CampaignAPIModal = ({ isOpen, onClose, campaign }) => {
    const [showJWTToken, setShowJWTToken] = useState(false);
    const [showClientSecret, setShowClientSecret] = useState(false);

    if (!isOpen || !campaign) return null;

    // API details based on actual redeemCoupon endpoint
    const apiDetails = {
        baseUrl: baseUrl,
        endpoint: '/api/hooks/coupon-redeemed',
        method: 'POST',
        jwtToken: process.env.REACT_APP_ACCESS_TOKEN_KEY || 'e99dd7a6250d09484402a061f01d1698bfb30a57bcec3b3f70ecbdd6c8258eb0',
        clientSecret: campaign.uniqueId || campaign.clientSecret || 'cs_live_51K3vIhJT2eRqNu8v8R7FgXyZ9WmN2Qs4E8V7B6',
        authKey: process.env.new_secret_key || 'your_auth_key_here',
        webhookBody: {
            orderId: "ORD_" + Math.random().toString(36).substr(2, 9),
            couponCode: "COUPON123",
            productName: "Example Product Name",
            brandName: "Example Brand",
            redemption: new Date().toISOString(),
            invoiceNumber: "INV_" + Math.random().toString(36).substr(2, 9),
            salesValue: 1999.99,
            locationCity: "Mumbai",
            userId: "507f1f77bcf86cd799439011",
            productId: "507f1f77bcf86cd799439012"
        },
        successResponse: {
            status: 200,
            success: true,
            message: "Coupon redeemed successfully.",
            data: {},
            timestamp: new Date().toISOString(),
            requestId: 'req_' + Math.random().toString(36).substr(2, 9)
        },
        errorResponses: {
            authKeyError: {
                status: 401,
                success: false,
                message: "You are not authorized to make this request.(AK-401)"
            },
            clientSecretError: {
                status: 401,
                success: false,
                message: "You are not authorized to make this request.(CS-401)"
            },
            missingFieldsError: {
                status: 400,
                success: false,
                message: "One or more Required field(s) missing.(RF-400)"
            },
            invalidCouponError: {
                status: 422,
                success: false,
                message: "Invalid coupon code.(CC-422)"
            },
            redemptionLimitError: {
                status: 400,
                success: false,
                message: "Coupon redemption limit reached.(CRL-400)"
            },
            alreadyRedeemedError: {
                status: 409,
                success: false,
                message: "Coupon already redeemed.(CR-409)"
            },
            campaignNotFoundError: {
                status: 403,
                success: false,
                message: "You are not authorized to make this request.(CS-403)"
            }
        }
    };

    const downloadPDF = async () => {
        const element = document.getElementById('api-modal-content');
        const canvas = await html2canvas(element, {
            scale: 2,
            useCORS: true,
            allowTaint: true
        });

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgWidth = 210;
        const pageHeight = 295;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;

        let position = 0;

        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
        }

        pdf.save(`campaign-api-details-${campaign.title}.pdf`);
    };

    const maskString = (str, visibleLength = 4) => {
        if (!str) return '';
        return '*'.repeat(Math.max(0, str.length - visibleLength)) + str.slice(-visibleLength);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <div>
                        <h2 className="text-xl font-bold text-darkGrayBg">Campaign API Details</h2>
                        <p className="text-sm text-gray-600 mt-1">{campaign.title}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={downloadPDF}
                            className="flex items-center gap-2 px-4 py-2 bg-chipBlue text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                            <FaDownload size={14} className='fill-white' />
                            Download PDF
                        </button>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 text-2xl"
                        >
                            <FaTimes />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div id="api-modal-content" className="p-6 space-y-6">
                    {/* API Endpoint Section */}
                    <div className="border border-border rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-darkGrayBg mb-4">API Endpoint</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Base URL</label>
                                <div className="p-3 bg-navBtn rounded border border-border font-mono text-sm">
                                    {apiDetails.baseUrl}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint</label>
                                <div className="p-3 bg-navBtn rounded border border-border font-mono text-sm">
                                    {apiDetails.endpoint}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Method</label>
                                <div className="p-3 bg-navBtn rounded border border-border font-mono text-sm text-green-800">
                                    {apiDetails.method}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Headers Section */}
                    <div className="border border-border rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-darkGrayBg mb-4">Headers</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">auth-key (Required)</label>
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 p-3 bg-navBtn rounded border border-border font-mono text-sm text-wrap overflow-scroll w-full">
                                        {showJWTToken ? apiDetails.jwtToken : maskString(apiDetails.jwtToken, 8)}
                                    </div>
                                    <button
                                        onClick={() => setShowJWTToken(!showJWTToken)}
                                        className="p-2 text-gray-600 hover:text-gray-800 border rounded"
                                    >
                                        {showJWTToken ? <FaEyeSlash /> : <FaEye />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">client-secret (Required)</label>
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 p-3 bg-navBtn rounded border border-border font-mono text-sm">
                                        {showClientSecret ? apiDetails.clientSecret : maskString(apiDetails.clientSecret, 6)}
                                    </div>
                                    <button
                                        onClick={() => setShowClientSecret(!showClientSecret)}
                                        className="p-2 text-gray-600 hover:text-gray-800 border rounded"
                                    >
                                        {showClientSecret ? <FaEyeSlash /> : <FaEye />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Content-Type</label>
                                <div className="p-3 bg-navBtn rounded border border-border font-mono text-sm">
                                    application/json
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Request Body Section */}
                    <div className="border border-border rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-darkGrayBg mb-4">Request Body</h3>
                        <div className="bg-darkGrayBg text-primary p-4 rounded font-mono text-sm overflow-x-auto">
                            <pre>{JSON.stringify(apiDetails.webhookBody, null, 2)}</pre>
                        </div>
                    </div>

                    {/* Response Section */}
                    <div className="border border-border rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-darkGrayBg mb-4">Response Examples</h3>
                        
                        {/* Success Response */}
                        <div className="mb-6">
                            <h4 className="text-md font-medium text-green-700 mb-2">✅ Success Response (200)</h4>
                            <div className="bg-darkGrayBg text-primary p-4 rounded font-mono text-sm overflow-x-auto">
                                <pre>{JSON.stringify(apiDetails.successResponse, null, 2)}</pre>
                            </div>
                        </div>

                        {/* Error Responses */}
                        <div className="space-y-4">
                            <h4 className="text-md font-medium text-red-700 mb-3">❌ Error Response Examples</h4>
                            
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm font-medium text-gray-700 mb-2">Invalid Auth Key (401)</p>
                                    <div className="bg-darkGrayBg text-red p-3 rounded font-mono text-xs overflow-x-auto">
                                        <pre>{JSON.stringify(apiDetails.errorResponses.authKeyError, null, 2)}</pre>
                                    </div>
                                </div>
                                
                                <div>
                                    <p className="text-sm font-medium text-gray-700 mb-2">Invalid Client Secret (401)</p>
                                    <div className="bg-darkGrayBg text-red p-3 rounded font-mono text-xs overflow-x-auto">
                                        <pre>{JSON.stringify(apiDetails.errorResponses.clientSecretError, null, 2)}</pre>
                                    </div>
                                </div>
                                
                                <div>
                                    <p className="text-sm font-medium text-gray-700 mb-2">Missing Required Fields (400)</p>
                                    <div className="bg-darkGrayBg text-red p-3 rounded font-mono text-xs overflow-x-auto">
                                        <pre>{JSON.stringify(apiDetails.errorResponses.missingFieldsError, null, 2)}</pre>
                                    </div>
                                </div>
                                
                                <div>
                                    <p className="text-sm font-medium text-gray-700 mb-2">Invalid Coupon Code (422)</p>
                                    <div className="bg-darkGrayBg text-red p-3 rounded font-mono text-xs overflow-x-auto">
                                        <pre>{JSON.stringify(apiDetails.errorResponses.invalidCouponError, null, 2)}</pre>
                                    </div>
                                </div>
                                
                                <div>
                                    <p className="text-sm font-medium text-gray-700 mb-2">Redemption Limit Reached (400)</p>
                                    <div className="bg-darkGrayBg text-red p-3 rounded font-mono text-xs overflow-x-auto">
                                        <pre>{JSON.stringify(apiDetails.errorResponses.redemptionLimitError, null, 2)}</pre>
                                    </div>
                                </div>
                                
                                <div>
                                    <p className="text-sm font-medium text-gray-700 mb-2">Already Redeemed (409)</p>
                                    <div className="bg-darkGrayBg text-red p-3 rounded font-mono text-xs overflow-x-auto">
                                        <pre>{JSON.stringify(apiDetails.errorResponses.alreadyRedeemedError, null, 2)}</pre>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Required Fields Section */}
                    <div className="border border-border rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-darkGrayBg mb-4">Required Fields & Validation</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h4 className="font-medium text-darkGrayBg mb-3">📋 Required Headers</h4>
                                <ul className="text-sm text-gray-600 space-y-2">
                                    <li className="flex items-center gap-2">
                                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                        <code className="bg-gray-100 px-2 py-1 rounded">auth-key</code> - System authentication key
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                        <code className="bg-gray-100 px-2 py-1 rounded">client-secret</code> - Campaign unique identifier
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                        <code className="bg-gray-100 px-2 py-1 rounded">Content-Type</code> - application/json
                                    </li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="font-medium text-darkGrayBg mb-3">📝 Required Body Fields</h4>
                                <ul className="text-sm text-gray-600 space-y-2">
                                    <li className="flex items-center gap-2">
                                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                        <code className="bg-gray-100 px-2 py-1 rounded">orderId</code> - Unique order identifier
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                        <code className="bg-gray-100 px-2 py-1 rounded">couponCode</code> - Coupon code to redeem
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                        <code className="bg-gray-100 px-2 py-1 rounded">redemption</code> - Redemption timestamp (ISO string)
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                        <code className="bg-gray-100 px-2 py-1 rounded">userId</code> - User ObjectId
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                        <code className="bg-gray-100 px-2 py-1 rounded">productId</code> - Product ObjectId
                                    </li>
                                </ul>
                            </div>
                        </div>
                        
                        <div className="mt-6">
                            <h4 className="font-medium text-darkGrayBg mb-3">🔍 Optional Fields</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="text-sm text-gray-600">
                                    <code className="bg-blue-100 px-2 py-1 rounded text-blue-800">productName</code>
                                </div>
                                <div className="text-sm text-gray-600">
                                    <code className="bg-blue-100 px-2 py-1 rounded text-blue-800">brandName</code>
                                </div>
                                <div className="text-sm text-gray-600">
                                    <code className="bg-blue-100 px-2 py-1 rounded text-blue-800">invoiceNumber</code>
                                </div>
                                <div className="text-sm text-gray-600">
                                    <code className="bg-blue-100 px-2 py-1 rounded text-blue-800">salesValue</code>
                                </div>
                                <div className="text-sm text-gray-600">
                                    <code className="bg-blue-100 px-2 py-1 rounded text-blue-800">locationCity</code>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <h4 className="font-medium text-yellow-800 mb-2">⚠️ Business Rules</h4>
                            <ul className="text-sm text-yellow-700 space-y-1">
                                <li>• Each coupon can only be redeemed once per user</li>
                                <li>• Campaign must have redemption slots available</li>
                                <li>• Transaction is atomic - either everything succeeds or nothing changes</li>
                                <li>• System tracks redemption percentage for monitoring</li>
                                <li>• All requests are logged for audit purposes</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CampaignAPIModal;
